// Shared "chrome" (standardized header + page-number footer) for jsPDF reports.
//
// Standard layout (drawn on the report's first/cover page):
//   - Top-right: "Generated: <date time>" and "By: <user>"
//   - Centered: report title
//   - Centered (optional): date range below the title
//   - A divider rule, with body content beginning beneath it
// Page numbers ("Page X of X") are stamped centered at the bottom of every page.
//
// Works in whatever unit the jsPDF doc uses (mm or pt) by converting point-based
// offsets through doc.internal.scaleFactor (points per unit).
import { format } from "date-fns";

export const formatGeneratedAt = (d = new Date()) => format(d, "MMM d, yyyy h:mm a");

export const dateRangeText = (dateRange) => {
  if (!dateRange) return "";
  if (typeof dateRange === "string") return dateRange;
  const { start, end } = dateRange;
  if (!start || !end) return "";
  return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
};

/**
 * Draw the standardized header on the CURRENT jsPDF page.
 * @returns {number} Y coordinate (doc units) where body content can begin.
 */
export const drawReportHeader = (
  doc,
  { title = "Report", dateRange, generatedBy = "Unknown", generatedAt = new Date() } = {}
) => {
  const sf = doc.internal.scaleFactor; // points per unit
  const u = (pt) => pt / sf; // points -> doc units
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  const marginX = u(40);

  doc.setTextColor(0, 0, 0);

  // Top-right: generation date + user
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Generated: ${formatGeneratedAt(generatedAt)}`, pageWidth - marginX, u(24), {
    align: "right",
  });
  doc.text(`By: ${generatedBy}`, pageWidth - marginX, u(36), { align: "right" });

  // Centered title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, centerX, u(66), { align: "center" });

  // Centered date range (optional)
  const rangeStr = dateRangeText(dateRange);
  let dividerY = u(84);
  if (rangeStr) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(rangeStr, centerX, u(86), { align: "center" });
    dividerY = u(100);
  }

  // Divider
  doc.setDrawColor(200);
  doc.setLineWidth(u(0.75));
  doc.line(marginX, dividerY, pageWidth - marginX, dividerY);

  doc.setTextColor(0, 0, 0);
  return dividerY + u(16); // content start
};

/**
 * Stamp "Page X of X" centered at the bottom of every page.
 * Call once after all pages have been added.
 */
export const stampPageNumbers = (doc) => {
  const sf = doc.internal.scaleFactor;
  const u = (pt) => pt / sf;
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Page ${i} of ${total}`, pageWidth / 2, pageHeight - u(18), {
      align: "center",
    });
  }
  doc.setTextColor(0);
};
